import { useEffect, useState, useCallback } from 'react';
import type { ReactNode } from 'react';
import type { User, Session } from '@supabase/supabase-js';
import { supabase } from '../lib/supabase/client';
import { AuthContext, type Organization } from './auth-context';

const ORG_STORAGE_KEY = 'snapagent_current_org';

export function AuthProvider({ children }: { children: ReactNode }) {
  const [user, setUser] = useState<User | null>(null);
  const [session, setSession] = useState<Session | null>(null);
  const [loading, setLoading] = useState(true);
  const [organizations, setOrganizations] = useState<Organization[]>([]);
  const [currentOrganization, setCurrentOrganization] = useState<Organization | null>(null);

  const fetchOrganizations = useCallback(async (userId: string) => {
    try {
      const { data: memberships, error } = await supabase
        .from('organization_members')
        .select('organization_id, role, organizations(id, name, slug, owner_id)')
        .eq('user_id', userId);

      if (error) throw error;

      const orgs: Organization[] = (memberships || [])
        .filter((m: any) => m.organizations)
        .map((m: any) => ({
          id: m.organizations.id,
          name: m.organizations.name,
          slug: m.organizations.slug,
          owner_id: m.organizations.owner_id,
          role: m.role || 'member',
        }));

      setOrganizations(orgs);

      // Restore saved org or default to first
      const savedOrgId = localStorage.getItem(ORG_STORAGE_KEY);
      const savedOrg = orgs.find(o => o.id === savedOrgId);
      
      if (savedOrg) {
        setCurrentOrganization(savedOrg);
      } else if (orgs.length > 0) {
        setCurrentOrganization(orgs[0]);
        localStorage.setItem(ORG_STORAGE_KEY, orgs[0].id);
      }
    } catch (err) {
      console.error('Failed to fetch organizations:', err);
    }
  }, []);

  const refreshOrganizations = useCallback(async () => {
    if (user) {
      await fetchOrganizations(user.id);
    }
  }, [user, fetchOrganizations]);

  const switchOrganization = useCallback((orgId: string) => {
    const org = organizations.find(o => o.id === orgId);
    if (org) {
      setCurrentOrganization(org);
      localStorage.setItem(ORG_STORAGE_KEY, orgId);
    }
  }, [organizations]);

  useEffect(() => {
    // Set up auth listener FIRST
    const { data: { subscription } } = supabase.auth.onAuthStateChange(
      (_event, currentSession) => {
        setSession(currentSession);
        setUser(currentSession?.user ?? null);
        setLoading(false);

        if (currentSession?.user) {
          // Defer to avoid deadlock
          setTimeout(() => fetchOrganizations(currentSession.user.id), 0);
        } else {
          setOrganizations([]);
          setCurrentOrganization(null);
        }
      }
    );

    // Then get initial session
    supabase.auth.getSession().then(({ data: { session: currentSession } }) => {
      setSession(currentSession);
      setUser(currentSession?.user ?? null);
      setLoading(false);

      if (currentSession?.user) {
        fetchOrganizations(currentSession.user.id);
      }
    });

    return () => {
      subscription.unsubscribe();
    };
  }, [fetchOrganizations]);

  const signOut = async () => {
    localStorage.removeItem(ORG_STORAGE_KEY);
    await supabase.auth.signOut();
  };

  return (
    <AuthContext.Provider value={{
      user,
      session,
      loading,
      signOut,
      organizations,
      currentOrganization,
      switchOrganization,
      refreshOrganizations,
    }}>
      {children}
    </AuthContext.Provider>
  );
}
